import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useReactive } from 'ahooks';
import { Col, Collapse, List, Row, Switch, message } from 'antd';
import { Button } from 'antd';
import _, { cloneDeep, filter, isEqual, map } from 'lodash';
import moment from 'moment';

import { ScheduleTemplateResp } from '@api/responseModels/worker/schedule/scheduleTemplateResponse';
import { Loader } from '@atoms/loader';
import { CRMAPIManager } from '@classes/crmApiManager';
import { messageService } from '@classes/messageService';
import ProfileBreadcrumbs from '@components/common/profileBreadcrumbs';
import {
    faCalendar,
    faCaretDown,
    faCaretRight,
    faUserTie,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FunctionalMenu } from '@molecules/functionalMenu';

import { weekDays } from './common/constants';
import { getFunctionalMenuItems } from './common/getFunctionalMenuItems';
import { getStartDate } from './common/helpers';
import {
    DEFAULT_DAYS_BY_MODE,
    getScheduleMode,
    SCHEDULE_TYPE_BY_MODE,
    TScheduleMode,
} from './common/scheduleMode';
import { TSchedule, TScheduleTemplate } from './common/types';
import { useAddBreak } from './hooks/useAddBreak';
import { useCreateSchedule } from './hooks/useCreateSchedule';
import { useLoadSchedule } from './hooks/useLoadSchedule';
import { useLoadTemplates } from './hooks/useLoadTemplates';
import { useNormalizeEDs } from './hooks/useNormalizeEds';
import { useResetEDs } from './hooks/useResetEds';
import { useSaveAndApplyTemplate } from './hooks/useSaveAndApplyTemplate';
import { useSaveScheduleTemplate } from './hooks/useSaveScheduleTemplate';
import { useUpdateSchedule } from './hooks/useUpdateSchedule';
import { useUpdateScheduleFromTemplate } from './hooks/useUpdateScheduleFromTemplate';
import { useUpdateTemplate } from './hooks/useUpdateTemplate';
import { NewTemplateModal } from './partials/newTemplateModal/newTemplateModal';
import { ScheduleExceptionsPanel } from './partials/scheduleExceptionsPanel/scheduleExceptionsPanel';
import { ScheduleBreaks } from './partials/scheduleSettingsPanel/scheduleBreaks';
import { ScheduleSettingsPanel } from './partials/scheduleSettingsPanel/scheduleSettingsPanel';
import { ScheduleTimeRow } from './partials/scheduleSettingsPanel/scheduleTimeRow';
import { TScheduleType } from './partials/scheduleSettingsPanel/scheduleTypeRow';
import { ScheduleTemplatesPanel } from './partials/scheduleTemplatesPanel/scheduleTemplatesPanel';

import './schedule.scss';

const { Panel } = Collapse;

export enum ScheduleUseCases {
    WorkerPosition_View = 'wp-view',
    WorkerPosition_Edit = 'wp-edit',
    CRM_View = 'crm-view',
    CRM_Edit = 'crm-edit',
    Template_Edit = 'template-edit',
}

type TProps = {
    useCase?: ScheduleUseCases;
};

export type TState = {
    isLoading: boolean;
    schedule?: TSchedule | TScheduleTemplate;
    scheduleOrigin?: TSchedule | TScheduleTemplate;
    scheduleImmutable?: TSchedule | TScheduleTemplate;
    scheduleTemplates: TScheduleTemplate[];
    /** Прокладка, собирающая exception_days, day_offs, schedule_days */
    eds: TScheduleTemplate['exception_days'];
    edsImmutable: TScheduleTemplate['exception_days'];
    tempBreaks: { start_break_time?: Date; end_break_time?: Date }[];
    /**
     * Режим работы в интерфейсе. Отдельно от `schedule_type`, потому что
     * «Каждый день» — пресет над недельным типом, а не собственный тип на бэке.
     */
    scheduleMode: TScheduleMode;
    intOn: number;
    intOff: number;
    wpName: string;
    templatesMeta: any;
    templatesCurrentPage: number;
};

// TEST

export type TScheduleTemplatePanelState = {
    isOpen: boolean;
    selectedTemplateId?: number;
    isEditing: boolean;
    isCreating: boolean;
    activeTemplateNameMirror: string;
    suggestedName: string;
    tempBreak: { start_break_time?: Date; end_break_time?: Date };
};

const Schedule = ({ useCase }: TProps): JSX.Element => {
    const state = useReactive<TState>({
        isLoading: false,
        schedule: null,
        scheduleOrigin: null,
        scheduleTemplates: [],
        eds: [],
        edsImmutable: [],
        tempBreaks: [],
        scheduleMode: 'week',
        intOn: 0,
        intOff: 0,
        // Храним начальное значение графика, чтобы ослеживать момент изменения
        scheduleImmutable: null,
        wpName: '',
        templatesMeta: null,
        templatesCurrentPage: 1,
    });
    const stpState = useReactive<TScheduleTemplatePanelState>({
        isOpen: false,
        selectedTemplateId: null,
        isEditing: false,
        isCreating: false,
        activeTemplateNameMirror: '',
        suggestedName: '',
        tempBreak: { start_break_time: null, end_break_time: null },
    });

    const isTemplateNameChanged = state.schedule?.title !== stpState.activeTemplateNameMirror;

    const [newTemplateModalVisible, setNewTemplateModalVisible] = useState<boolean>(false);
    const [newTemplateName, setNewTemplateName] = useState<string>('');

    const { crmID, workerPositionID } = useParams();
    const navigate = useNavigate();
    const [messageApi, contextHolder] = message.useMessage();
    const editing =
        useCase == ScheduleUseCases.CRM_Edit ||
        useCase == ScheduleUseCases.WorkerPosition_Edit ||
        useCase == ScheduleUseCases.Template_Edit;
    const wpMode =
        useCase == ScheduleUseCases.WorkerPosition_Edit ||
        useCase == ScheduleUseCases.WorkerPosition_View;
    // Находимся ли мы в режиме настройки шаблонов
    const tpEditMode = useCase == ScheduleUseCases.Template_Edit;
    // У CRM/рабочего места ещё нет графика — форма работает на создание (POST), а не обновление (PUT)
    const isNewSchedule = !tpEditMode && state.schedule?.id == null;

    const hideSchedulePanels = tpEditMode && !stpState.isCreating && !stpState.selectedTemplateId;

    // Отслеживаем изменения в графике
    const scheduleChanged = useMemo(
        () =>
            !isEqual(state.schedule, state.scheduleImmutable) ||
            !isEqual(state.eds, state.edsImmutable),
        [JSON.stringify(state.schedule), JSON.stringify(state.eds)]
    );

    function validateSchedule() {
        normalizeEDs();
        state.schedule.title = stpState.activeTemplateNameMirror;
        state.schedule.crm_id = +crmID;
        if (state.schedule.start_time == null) {
            messageApi.warning('Задайте начало рабочего дня!');
            return false;
        }
        if (state.schedule.end_time == null) {
            messageApi.warning('Задайте конец рабочего дня!');
            return false;
        }
        return true;
    }

    const { resetEDs } = useResetEDs(state);
    const { loadSchedule } = useLoadSchedule({
        crmID,
        state,
        stpState,
        useCase,
        workerPositionID,
        messageApi,
    });
    const { loadTemplates } = useLoadTemplates({ crmID, messageApi, state });
    const { normalizeEDs } = useNormalizeEDs(state);
    const { updateSchedule } = useUpdateSchedule({
        crmID,
        messageApi,
        state,
        workerPositionID,
        wpMode,
        validateSchedule,
    });
    const { createSchedule } = useCreateSchedule({
        crmID,
        messageApi,
        state,
        workerPositionID,
        wpMode,
        validateSchedule,
    });
    const saveSchedule = isNewSchedule ? createSchedule : updateSchedule;
    const { saveAsTemplate } = useSaveScheduleTemplate({
        crmID,
        messageApi,
        messageService,
        state,
        stpState,
        tpEditMode,
        validateSchedule,
        workerPositionID,
    });
    const { saveAsTemplateAndUpdateScheduleFromTemplate } = useSaveAndApplyTemplate({
        crmID,
        messageApi,
        messageService,
        state,
        validateSchedule,
        workerPositionID,
        stpState,
    });
    const { updateTemplate } = useUpdateTemplate({
        crmID,
        messageApi,
        state,
        validateSchedule,
        workerPositionID,
        wpMode,
        isTemplateNameChanged,
    });
    const { updateScheduleFromTemplate } = useUpdateScheduleFromTemplate({
        crmID,
        getStartDate,
        messageApi,
        state,
        stpState,
        workerPositionID,
    });
    const { tryAddBreak } = useAddBreak(state, messageApi);

    function handleEditing() {
        if (wpMode) {
            navigate(`/lk/worker/crm/${crmID}/workers/${workerPositionID}/schedule/edit`);
        } else {
            navigate(`/lk/worker/crm/${crmID}/schedule/edit`);
        }
    }

    function handleAbortEditing() {
        stpState.selectedTemplateId = null;
        if (wpMode) {
            navigate(`/lk/worker/crm/${crmID}/workers/${workerPositionID}/schedule`);
        } else {
            navigate(`/lk/worker/crm/${crmID}/schedule`);
        }
    }

    const fMenuItems = getFunctionalMenuItems({
        editing,
        handleAbortEditing,
        handleEditing,
        saveAsTemplate,
        scheduleChanged,
        setNewTemplateModalVisible,
        setNewTemplateName,
        stpState,
        suggestScheduleName,
        tpEditMode,
        updateSchedule: saveSchedule,
        updateScheduleFromTemplate,
        updateTemplate,
        wpMode,
        wpName: state.wpName,
        isTemplateNameChanged,
        isNewSchedule,
    });

    function getCrumbs() {
        let crumbs = [];
        if (wpMode) {
            crumbs.push(
                {
                    label: 'Сотрудники',
                    icon: faUserTie,
                    navTarget: `/lk/worker/crm/${crmID}/workers/tabs/working-now`,
                },
                {
                    label: `ID: ${workerPositionID}`,
                    navTarget: `/lk/worker/crm/${crmID}/workers/${workerPositionID}`,
                }
            );
        }
        crumbs.push({
            label: 'График',
            icon: faCalendar,
        });
        return crumbs;
    }

    const reloadTemplates = async () => {
        state.templatesCurrentPage = 1;
        state.scheduleTemplates = [];
        state.templatesMeta = null;
        await loadTemplates();
    };

    const onTemplatesPageChange = async () => {
        state.templatesCurrentPage += 1;
        await loadTemplates(state.templatesCurrentPage);
    };

    useEffect(() => {
        if (!tpEditMode) {
            loadSchedule().then(() => {
                if (wpMode && editing) reloadTemplates();
            });
            return;
        }
        // Загружаем шаблоны без предварительной загрузки графика если мы находимся в режиме настройки шаблонов
        // Сетим дефолтные значения при первом рендере, когда шаблон не выбран
        if (tpEditMode) {
            state.schedule = {
                id: null,
                title: '',
                start_time: '08:00',
                end_time: '17:00',
                breaks: [
                    {
                        start_break_time: '12:00',
                        end_break_time: '13:00',
                    },
                ],
                day_offs: [],
                schedules: [],
                schedule_days: [],
                schedule: [],
                schedule_type: 0,
                crm_id: +crmID,
                exception_days: [],
                is_basic: 0,
                master_template: 0,
            };
            stpState.activeTemplateNameMirror = '';
            stpState.selectedTemplateId = null;
            stpState.isOpen = true;
            state.scheduleMode = getScheduleMode(state.schedule);
            resetEDs();
            reloadTemplates();
        }
    }, [wpMode, editing, tpEditMode]);

    function onTemplateSelect(templateId: number) {
        const template = state.scheduleTemplates.find((t) => t.id == templateId);
        if (!template) return;
        if (template.id == stpState.selectedTemplateId) {
            stpState.selectedTemplateId = null;
            stpState.activeTemplateNameMirror = state.scheduleOrigin.title;
            state.schedule = state.scheduleOrigin;
            state.scheduleOrigin = null;
            // if (tpEditMode) {
            state.scheduleImmutable = cloneDeep(state.schedule);
            // }
            state.scheduleMode = getScheduleMode(state.schedule);
            resetEDs();
        } else {
            stpState.selectedTemplateId = templateId;
            stpState.activeTemplateNameMirror = template.title;
            state.scheduleOrigin = state.schedule;
            state.schedule = template;
            // if (tpEditMode) {
            state.scheduleImmutable = cloneDeep(state.schedule);
            // }
            state.scheduleMode = getScheduleMode(state.schedule);
            resetEDs();
        }
    }

    async function onTemplateDelete(templateId: number) {
        const template = state.scheduleTemplates.find((t) => t.id == templateId);
        if (!template) return;
        if (template.schedules.length > 0) {
            messageApi.info(`По этому шаблону работают ${template.schedules.length} рабочих мест.`);
            return;
        }
        if (templateId == stpState.selectedTemplateId) onTemplateSelect(templateId);
        state.isLoading = true;
        try {
            const result = await CRMAPIManager.request<ScheduleTemplateResp>(async (api) => {
                return await api.removeScheduleTemplate(+crmID, templateId);
            });
            if (result.errorCode) throw result;
            await reloadTemplates();
        } catch (err) {
            messageApi.error('Ошибка при удалении шаблона');
            console.log(err);
        }
        state.isLoading = false;
    }

    function suggestScheduleName() {
        let suggestion = '';
        switch (state.scheduleMode) {
            case 'daily': {
                suggestion = 'Ежедневно';
                break;
            }
            case 'interval': {
                suggestion = `${state.schedule?.schedule_days?.length}/${state.schedule.day_offs?.length}`;
                break;
            }
            case 'week': {
                suggestion = '5/2';
                break;
            }
        }
        suggestion += ` (${state.schedule?.start_time}-${state.schedule?.end_time})`;
        stpState.suggestedName = suggestion;
        return suggestion;
    }

    useEffect(() => {
        if (
            state.schedule != null &&
            stpState.activeTemplateNameMirror.trim() == '' &&
            stpState.selectedTemplateId == null
        ) {
            suggestScheduleName();
        }
    }, [
        state.scheduleMode,
        state.intOn,
        state.intOff,
        state.schedule?.start_time,
        state.schedule?.end_time,
        stpState.activeTemplateNameMirror,
    ]);

    function onNameChange(value: string) {
        stpState.activeTemplateNameMirror = value;
    }

    function setDayOff(index: number, is_day_off: boolean) {
        let tExcDays = state.eds;
        tExcDays[index].is_day_off = Number(is_day_off);
        state.eds = tExcDays;
    }

    function onExceptionDayTimeChange(
        index: number,
        value: string,
        target: 'start_time' | 'end_time'
    ) {
        let tExcDays = state.eds;
        tExcDays[index][target] = value;
        state.eds = tExcDays;
    }

    function onBreakDelete(index: number, bIndex: number) {
        let tExcDays = state.eds;
        tExcDays[index].breaks = tExcDays[index].breaks.splice(bIndex, 1);
        state.eds = tExcDays;
    }

    function onTempBreakChange(
        index: number,
        val: Date | null,
        target: 'start_break_time' | 'end_break_time'
    ) {
        let tTempBreaks = state.tempBreaks;
        tTempBreaks[index][target] = val;
        state.tempBreaks = tTempBreaks;
    }

    function onScheduleModeChange(option: TScheduleType | TScheduleType[]) {
        const mode = (option as TScheduleType).id;
        const { on, off } = DEFAULT_DAYS_BY_MODE[mode];

        state.scheduleMode = mode;
        state.schedule.schedule_type = SCHEDULE_TYPE_BY_MODE[mode];
        resetEDs(on, off);
    }

    function onStartDateChange(value: moment.Moment) {
        (state.schedule as TSchedule).start_schedule_at = value.format('YYYY-MM-DD');
    }

    function setScheduleIntervalOnDays(value: number) {
        resetEDs(value, state.intOff);
    }

    function setScheduleIntervalOffDays(value: number) {
        resetEDs(state.intOn, value);
    }

    function onScheduleTimeChange(value: string, target: 'start_time' | 'end_time') {
        state.schedule[target] = value;
        // Сбрасываем все перерывы если меняется основное время (Желание заказчика)
        state.schedule.breaks = [];
        state.eds = map(state.eds, (ed) => {
            if (ed.is_day_off == 0) {
                return {
                    ...ed,
                    [target]: value,
                    breaks: [],
                };
            }
            return ed;
        });
    }

    function onMainBreakDelete(index: number) {
        const tSchedule = state.schedule;
        const foundBreak = cloneDeep(state.schedule.breaks[index]);
        state.eds = filter(state.eds, (item) => {
            return !isEqual(item, foundBreak);
        });
        tSchedule.breaks.splice(index, 1);
        state.schedule = tSchedule;
    }

    function onTempBrakeChange(val: Date | null, target: 'start_break_time' | 'end_break_time') {
        stpState.tempBreak[target] = val;
    }

    const handleTemplateEditMode = () => {
        navigate(`/lk/worker/crm/${crmID}/schedule/templates/edit`);
    };

    return (
        <>
            <div id="schedule-profile">
                {contextHolder}
                {state.isLoading && <Loader />}
                <FunctionalMenu items={fMenuItems} dropdownItems={[]} />
                <ProfileBreadcrumbs
                    crmLinkText="CRM"
                    headerText={
                        tpEditMode
                            ? `Шаблоны графиков работы компании`
                            : `График работы ${wpMode ? state.wpName : 'CRM'}`
                    }
                    crumbs={getCrumbs()}
                />
                {((wpMode && editing) || tpEditMode) && (
                    <ScheduleTemplatesPanel
                        isOpen={stpState.isOpen}
                        flipIsOpen={() => (stpState.isOpen = !stpState.isOpen)}
                        templates={state.scheduleTemplates}
                        selectedTemplateId={stpState.selectedTemplateId}
                        onTemplateSelect={onTemplateSelect}
                        isEditing={stpState.isEditing}
                        isCreating={stpState.isCreating}
                        flipIsEditing={() => (stpState.isEditing = !stpState.isEditing)}
                        flipIsCreating={() => (stpState.isCreating = !stpState.isCreating)}
                        activeTemplateNameMirror={stpState.activeTemplateNameMirror}
                        changeActiveTemplateNameMirror={onNameChange}
                        suggestedName={stpState.suggestedName}
                        onTemplateDelete={onTemplateDelete}
                        canEdit={tpEditMode}
                        totalTemplates={state.templatesMeta?.total}
                        onTemplatesPageChange={onTemplatesPageChange}
                    />
                )}
                {!hideSchedulePanels && (
                    <>
                        <Collapse
                            bordered={false}
                            defaultActiveKey={['schedule', 'days']}
                            expandIcon={({ isActive }) => (
                                <FontAwesomeIcon
                                    className="bigger-caret"
                                    icon={isActive ? faCaretDown : faCaretRight}
                                />
                            )}
                            expandIconPlacement="end"
                            styles={{
                                header: {
                                    backgroundColor: 'rgb(255, 201, 140)',
                                    border: '2px solid rgb(154, 101, 59)',
                                    borderRadius: 12,
                                    color: 'black',
                                    fontSize: 16,
                                    fontWeight: 500,
                                    alignItems: 'center',
                                    cursor: 'default',
                                },
                                body: {
                                    backgroundColor: 'rgb(255, 201, 140)',
                                    border: '2px solid rgb(154, 101, 59)',
                                    borderTop: 'none',
                                    borderBottomLeftRadius: 12,
                                    borderBottomRightRadius: 12,
                                    padding: 0,
                                },
                            }}
                        >
                            <Panel
                                collapsible="disabled"
                                forceRender={true}
                                header={
                                    tpEditMode && stpState.selectedTemplateId
                                        ? stpState.activeTemplateNameMirror
                                        : tpEditMode && stpState.isCreating
                                        ? 'Создание нового шаблона:'
                                        : 'Рабочий график: ' +
                                          (stpState.selectedTemplateId
                                              ? stpState.activeTemplateNameMirror
                                              : state.schedule?.title ?? '')
                                }
                                key="schedule"
                                showArrow={false}
                            >
                                <ScheduleSettingsPanel
                                    editing={editing}
                                    schedule={state.schedule}
                                    scheduleTypes={[
                                        { id: 'week', label: 'По дням недели', value: 'week' },
                                        { id: 'interval', label: 'Интервал', value: 'interval' },
                                        { id: 'daily', label: 'Каждый день', value: 'daily' },
                                    ]}
                                    scheduleMode={state.scheduleMode}
                                    onScheduleTypeChange={onScheduleModeChange}
                                    onStartDateChange={onStartDateChange}
                                    setScheduleIntervalOnDays={setScheduleIntervalOnDays}
                                    setScheduleIntervalOffDays={setScheduleIntervalOffDays}
                                    onScheduleTimeChange={onScheduleTimeChange}
                                    onBreakDelete={onMainBreakDelete}
                                    tempBrake={{
                                        start_break_time: stpState.tempBreak.start_break_time,
                                        end_break_time: stpState.tempBreak.end_break_time,
                                    }}
                                    onTempBrakeChange={onTempBrakeChange}
                                    tryAddBreak={tryAddBreak}
                                    intOn={state.intOn}
                                    intOff={state.intOff}
                                />
                            </Panel>
                            {state.scheduleMode !== 'daily' && (
                                <Panel header="Настроить каждый день отдельно:" key="days">
                                    <List
                                        className="by-day-list"
                                        dataSource={state.eds}
                                        itemLayout="horizontal"
                                        renderItem={(item, index) => (
                                            <List.Item key={'li' + index}>
                                                <Col className="full-width">
                                                    <Row className="space-between">
                                                        <Col>
                                                            <b>
                                                                {state.schedule?.schedule_type == 0
                                                                    ? weekDays[+item.day - 1]
                                                                    : `${item.day} день`}
                                                            </b>
                                                        </Col>
                                                        <Col>
                                                            <label>Выходной</label>
                                                            <Switch
                                                                className="hmargined"
                                                                checked={item.is_day_off == 1}
                                                                disabled={!editing}
                                                                onChange={(checked) =>
                                                                    setDayOff(index, checked)
                                                                }
                                                            />
                                                        </Col>
                                                    </Row>
                                                    {item.is_day_off == false && (
                                                        <ScheduleTimeRow
                                                            editing={editing}
                                                            scheduleTime={{
                                                                start_time: item.start_time,
                                                                end_time: item.end_time,
                                                            }}
                                                            onScheduleTimeChange={(v, t) =>
                                                                onExceptionDayTimeChange(
                                                                    index,
                                                                    v,
                                                                    t
                                                                )
                                                            }
                                                        />
                                                    )}
                                                    {item.is_day_off == false && (
                                                        <ScheduleBreaks
                                                            editing={editing}
                                                            breaks={item.breaks}
                                                            tempBreak={state.tempBreaks[index]}
                                                            onBreakDelete={(bIndex) =>
                                                                onBreakDelete(index, bIndex)
                                                            }
                                                            onTempBreakChange={(v, t) =>
                                                                onTempBreakChange(index, v, t)
                                                            }
                                                            tryAddBreak={(_, d) =>
                                                                tryAddBreak(index, d)
                                                            }
                                                        />
                                                    )}
                                                </Col>
                                            </List.Item>
                                        )}
                                    />
                                </Panel>
                            )}
                        </Collapse>
                        <ScheduleExceptionsPanel editing={editing} />
                    </>
                )}
                {!wpMode && !tpEditMode && (
                    <Button className="crm-templates__button" onClick={handleTemplateEditMode}>
                        Шаблоны графиков работы компании
                    </Button>
                )}
            </div>
            <NewTemplateModal
                newTemplateModalVisible={newTemplateModalVisible}
                newTemplateName={newTemplateName}
                saveAsTemplate={saveAsTemplate}
                saveAsTemplateAndupdateScheduleFromTemplate={
                    saveAsTemplateAndUpdateScheduleFromTemplate
                }
                setNewTemplateModalVisible={setNewTemplateModalVisible}
                setNewTemplateName={setNewTemplateName}
            />
        </>
    );
};

export default Schedule;
